import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Car, Calendar, Trash2, LogOut, RefreshCw } from "lucide-react";
import { LOGO_URL } from "../landing/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rd_admin_token";
const STATUSES = ["new", "contacted", "completed"];
const STATUS_STYLES = {
  new: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  contacted: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [leads, setLeads] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const loadLeads = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/quotes`, authHeaders());
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired — please log in again.");
      } else {
        toast.error("Could not load leads.");
      }
    }
  }, [authHeaders]);

  useEffect(() => {
    if (token) loadLeads();
  }, [token, loadLeads]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success(`Welcome back, ${data.user.name}.`);
    } catch (err) {
      setLoginError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads(null);
  };

  const onStatusChange = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/quotes/${id}`, { status }, authHeaders());
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`, authHeaders());
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch {
      toast.error("Could not delete lead.");
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#0b101b] text-slate-100 flex items-center justify-center px-6">
        <div className="w-full max-w-sm bg-[#101826] border border-white/15 rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Revive & Drive Auto LLC logo" className="h-10 w-auto rounded border border-white/10" />
            <div>
              <p className="font-bold text-sm uppercase">Leads Dashboard</p>
              <p className="font-mono-lbl text-[10px] text-slate-400 uppercase tracking-widest">Admin Access</p>
            </div>
          </div>
          <form onSubmit={onLogin} data-testid="admin-login-form" className="space-y-4">
            <div>
              <label htmlFor="admin-email" className="block font-mono-lbl text-xs text-slate-300 uppercase mb-2">Email</label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={loginForm.email}
                onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full bg-[#080d15] border border-white/15 rounded-lg px-4 py-3 text-sm text-white focus:border-[#ff4b2b] focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block font-mono-lbl text-xs text-slate-300 uppercase mb-2">Password</label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={loginForm.password}
                onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
                className="w-full bg-[#080d15] border border-white/15 rounded-lg px-4 py-3 text-sm text-white focus:border-[#ff4b2b] focus:outline-none transition-colors"
              />
            </div>
            {loginError && (
              <p data-testid="admin-login-error" className="text-sm text-red-400">{loginError}</p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loggingIn}
              className="w-full bg-[#ff4b2b] hover:bg-[#ff3411] disabled:opacity-60 text-white py-3.5 rounded-lg font-bold uppercase text-sm tracking-wide transition-colors shadow-lg shadow-[#ff4b2b]/30"
            >
              {loggingIn ? "Signing In..." : "Sign In"}
            </button>
          </form>
          <Link to="/" data-testid="admin-back-home-link" className="block mt-6 text-center font-mono-lbl text-xs text-cyan-300 hover:text-cyan-200 uppercase tracking-widest transition-colors">
            &larr; Back to Website
          </Link>
        </div>
      </div>
    );
  }

  const counts = leads
    ? { total: leads.length, new: leads.filter((l) => l.status === "new").length }
    : { total: 0, new: 0 };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0b101b] text-slate-100">
      <nav className="border-b border-white/10 bg-[#0b101b]/90 backdrop-blur sticky top-0 z-50 px-6 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Revive & Drive Auto LLC logo" className="h-10 w-auto rounded border border-white/10" />
            <span className="font-bold text-base tracking-tight uppercase">Leads Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={loadLeads}
              data-testid="admin-refresh-button"
              className="border border-white/20 hover:border-white/50 p-2.5 rounded-lg transition-colors"
              title="Refresh leads"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <Link to="/" data-testid="admin-view-site-link" className="hidden sm:inline-block font-mono-lbl text-xs tracking-widest uppercase text-slate-300 hover:text-white transition-colors">
              View Site
            </Link>
            <button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 bg-[#ff4b2b] hover:bg-[#e03d1f] text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
            >
              <LogOut className="w-4 h-4" /> Log Out
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-mono-lbl text-xs uppercase tracking-widest text-[#00e5ff]">Quote Requests</p>
            <h1 className="text-3xl font-black uppercase tracking-tight mt-1">Incoming Leads</h1>
          </div>
          <div data-testid="admin-lead-counts" className="flex gap-4 font-mono-lbl text-xs uppercase">
            <span className="px-3 py-1.5 rounded-full border border-white/15 text-slate-300">{counts.total} Total</span>
            <span className="px-3 py-1.5 rounded-full border border-cyan-500/40 text-cyan-300">{counts.new} New</span>
          </div>
        </div>

        {leads === null ? (
          <p className="text-slate-400 text-sm">Loading leads...</p>
        ) : leads.length === 0 ? (
          <div data-testid="admin-empty-state" className="p-10 border border-dashed border-white/15 rounded-xl text-center text-slate-400 text-sm">
            No quote requests yet. New submissions from the website will appear here.
          </div>
        ) : (
          <div className="space-y-4">
            {leads.map((lead) => (
              <div
                key={lead.id}
                data-testid={`lead-card-${lead.id}`}
                className="bg-[#101726] border border-white/10 rounded-xl p-5 flex flex-col lg:flex-row lg:items-center gap-4 hover:border-white/20 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-bold text-lg">{lead.name}</span>
                    <span className={`px-2.5 py-0.5 rounded-full border text-[11px] font-mono-lbl uppercase ${STATUS_STYLES[lead.status] || STATUS_STYLES.new}`}>
                      {lead.status}
                    </span>
                  </div>
                  <p className="text-cyan-300 text-sm font-semibold mt-1">{lead.service}</p>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400">
                    <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                      <Phone className="w-3.5 h-3.5 text-[#ff4b2b]" /> {lead.phone}
                    </a>
                    {lead.email && (
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                        <Mail className="w-3.5 h-3.5 text-[#ff4b2b]" /> {lead.email}
                      </a>
                    )}
                    <span className="flex items-center gap-1.5"><Car className="w-3.5 h-3.5 text-[#00e5ff]" /> {lead.vehicle}</span>
                    <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#00e5ff]" /> {lead.location}</span>
                    {(lead.preferred_date || lead.preferred_time) && (
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-[#00e5ff]" />
                        {[lead.preferred_date, lead.preferred_time].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 font-mono-lbl text-[10px] text-slate-500 uppercase">
                    {new Date(lead.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <select
                    value={lead.status}
                    onChange={(e) => onStatusChange(lead.id, e.target.value)}
                    data-testid={`lead-status-select-${lead.id}`}
                    className="bg-[#080d15] border border-white/15 rounded-lg px-3 py-2 text-xs text-white focus:border-[#ff4b2b] focus:outline-none transition-colors"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => onDelete(lead.id)}
                    data-testid={`lead-delete-button-${lead.id}`}
                    className="border border-white/15 hover:border-red-500/60 hover:text-red-400 p-2.5 rounded-lg transition-colors"
                    title="Delete lead"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
